/**
 * Tab registry — how feature workstreams plug content into the shell.
 *
 * The shell owns the chrome (title bar, ribbon, docks, status bar) and nothing
 * else. Each ribbon tab may contribute a centre component and, optionally, the
 * left and right panel contents for that tab. Unregistered tabs render a
 * placeholder, so the app is always runnable while work lands in parallel.
 *
 * Usage from a feature module (e.g. `src/tabs/results/index.tsx`):
 *
 *     registerTab("results", { centre: ResultsCentre, right: ExportPanel });
 *
 * Modules under `src/tabs/**` are imported automatically by
 * `shell/bootstrap.ts`, so no wiring in App.tsx is needed.
 */

import type React from "react";
import { useSyncExternalStore } from "react";

export type TabId =
  | "file"
  | "home"
  /** The diffusion workflow: its steps beside the viewer. */
  | "dwi"
  | "pipeline"
  /** Reviewing a folder of subjects before any of them is opened. */
  | "dataset"
  | "preprocess"
  | "registration"
  | "segmentation"
  | "perfusion"
  | "quantification"
  /** Training, evaluating and comparing models. Nothing is built yet. */
  | "learning"
  | "view"
  /** Browsing and reading what a run produced — a top-level tab. */
  | "output"
  | "results"
  | "developer";

export interface TabModule {
  centre: React.ComponentType;
  left?: React.ComponentType;
  right?: React.ComponentType;
}

export const tabRegistry: Partial<Record<TabId, TabModule>> = {};

// Registration usually happens at import time, i.e. before the first render,
// but a lazily-loaded tab may register later — hence the subscription.
const listeners = new Set<() => void>();
let version = 0;

function emit(): void {
  version += 1;
  for (const l of listeners) l();
}

export function registerTab(id: TabId, mod: TabModule): void {
  tabRegistry[id] = mod;
  emit();
}

export function subscribeTabRegistry(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Re-renders when the registry changes; returns undefined for a free tab. */
export function useTabModule(id: TabId): TabModule | undefined {
  useSyncExternalStore(
    subscribeTabRegistry,
    () => version,
    () => version,
  );
  return tabRegistry[id];
}

// ------------------------------------------------------------- dock panels ---
// Additive to the contract above: the bottom dock is a fixed set of slots the
// shell draws, whose bodies a workstream can claim the same way.

export type DockTabId = "jobs" | "log" | "qc" | "console";

export const dockRegistry: Partial<Record<DockTabId, React.ComponentType>> = {};

export function registerDockPanel(id: DockTabId, component: React.ComponentType): void {
  dockRegistry[id] = component;
  emit();
}

export function useDockPanel(id: DockTabId): React.ComponentType | undefined {
  useSyncExternalStore(
    subscribeTabRegistry,
    () => version,
    () => version,
  );
  return dockRegistry[id];
}
